import { formatCents } from "../lib/money";

/* Progress toward the payout bar.
 *
 * This exists because of the threshold policy: a child can finish four jobs
 * out of five and earn NOTHING. A plain star count would hide that cliff, so
 * the bar draws the line itself and the caption says how many jobs are left to
 * cross it. Once cleared it stops being a progress bar and becomes a
 * confirmation -- there is no reward for overshooting, and pretending there is
 * would be a lie.
 */
export function ThresholdBar({
  earnedBp,
  thresholdBp,
  amountCents,
  jobsStillNeeded,
  met,
  payoutAt,
}: {
  earnedBp: number;
  thresholdBp: number;
  amountCents: number;
  jobsStillNeeded: number;
  met: boolean;
  payoutAt?: string;
}) {
  /* The count is for the whole PERIOD, not today. A weekly allowance with
   * daily chores means thirty-odd jobs, so "27 more jobs" under a list of
   * five reads as a punishment. Naming the week, and saying when it pays,
   * turns a demoralising number into a plan. */
  const paysOn = payoutAt
    ? new Date(payoutAt).toLocaleDateString(undefined, { weekday: "long" })
    : null;
  const pct = Math.min(100, earnedBp / 100);
  const markAt = Math.min(100, thresholdBp / 100);

  return (
    <div style={{ display: "grid", gap: 8 }}>
      {/* Glass, like the jars above it: the week's progress is a tube filling
          up on the same shelf, not a readout arriving from somewhere else. */}
      <div
        className="tube"
        role="progressbar"
        aria-valuenow={Math.round(earnedBp / 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Progress toward earning your allowance"
      >
        <div
          className="tube-fill"
          style={{
            width: `${pct}%`,
            background: met ? "var(--save)" : "var(--spend)",
          }}
        />
        {/* The bar to cross, printed on the glass itself. */}
        <span className="tube-mark" aria-hidden="true" style={{ left: `${markAt}%` }} />
        <span className="tube-gloss" aria-hidden="true" />
      </div>

      <p
        style={{
          margin: 0,
          fontFamily: "var(--font-display)",
          fontWeight: 600,
          fontSize: 16,
          color: met ? "var(--save-deep)" : "var(--ink-2)",
        }}
      >
        {met ? (
          <>
            You&rsquo;ve earned your {formatCents(amountCents)}
            {paysOn ? ` — it arrives ${paysOn}` : ""}!
          </>
        ) : jobsStillNeeded > 0 ? (
          <>
            {jobsStillNeeded} more {jobsStillNeeded === 1 ? "job" : "jobs"} this week
            to earn {formatCents(amountCents)}
          </>
        ) : (
          <>Keep going to earn {formatCents(amountCents)}</>
        )}
      </p>
    </div>
  );
}
